const {
    constrainPositionToBoundingBox,
    constrainDimensionToBoundingBox,
    isRotationDeltaBlocked,
    clampRotationDelta
} = require('../utils/geometryUtils.js');

// Fabric.js custom vector EPS object
fabric.VectorPlaceholder = fabric.util.createClass(fabric.Group, {
    async: true,
    type: 'vector-placeholder',
    cacheProperties: fabric.Group.prototype.cacheProperties.concat('stroke'),

    originX: 'center',
    originY: 'center',
    selectedPosition: 1,
    svgColor: '',

    svgString: '',
    pdf: null,

    rotationStep: 1,
    coverMaskArea: false,
    lockUniScalingWithSkew: false,

    preventEditMode: false,
    _editMode: false,
    _enablingEditMode: false,
    _editModeDataBackup: {},

    _boundingBoxOffsetX: 0,
    _boundingBoxOffsetY: 0,

    // Hard-clamps the vector content to stay inside the placeholder's static frame
    limitMovement: false,

    // While true, drags/scales/rotations of the control rectangle are both clamped to the
    //  placeholder's frame AND captured into initWidth/initHeight/initCenterX/initCenterY/initRotation.
    //  Set by the "Set Initial Size & Position" admin action.
    saveVectorPosition: false,

    initWidth: null,
    initHeight: null,
    initCenterX: null,
    initCenterY: null,
    initRotation: null,

    // Resolves once the currently loading svgString/svgImage/pdf has finished rendering and
    //  the control rectangle has been synced to it (including reapplying init values). Callers
    //  that set content and then immediately need the final position/scale must await this.
    _contentLoadPromise: null,

    initialize: function (options) {
        const scope = this;
        options.width = options.width || 300;
        options.height = options.height || 300;

        const boundsRectangle = new fabric.Rect({
            strokeDashArray: options.strokeDashArray || [10, 10],
            originX: 'center',
            originY: 'center',
            stroke: options.stroke || '#000000',
            strokeWidth: options.strokeWidth || 0,
            width: options.width,
            height: options.height,
            fill: 'rgba(0, 0, 0, 0)',
            id: options.id
        });

        // Create the rectangle used to move the vector around in edit mode
        const isMobile = !!options.isMobile;
        this._controlRectangle = new fabric.Rect({
            originX: 'center',
            originY: 'center',
            strokeWidth: 3,
            width: options.width,
            height: options.height,
            fill: 'rgba(0, 0, 0, 0)',
            id: options.id
        });

        this._controlRectangle.setControlsVisibility({
            tl: !isMobile,
            tr: !isMobile,
            br: !isMobile,
            bl: !isMobile,
            mtr: !isMobile,

            ml: false,
            mb: false,
            mr: false,
            mt: false
        });

        this.callSuper(
            'initialize',
            [
                new fabric.SVGObject({
                    width: options.width,
                    height: options.height,
                    visible: true,
                    originX: 'center',
                    originY: 'center'
                }),
                new fabric.PDFObject({
                    width: options.width,
                    height: options.height,
                    visible: false,
                    originX: 'center',
                    originY: 'center'
                }),
                boundsRectangle
            ],
            options
        );

        // Center the bound rectangle in the group
        boundsRectangle.set('left', 0);
        boundsRectangle.set('top', 0);
        boundsRectangle.setCoords();
        this._resetControlRectangle();

        this._controlRectangle.on({
            mousedown: function (e) {
                // Refresh the rotation baseline at the start of every interaction so a
                //  rejected rotation reverts to the current angle.
                this._lastValidAngle = this.angle;
                scope.canvas.fire('object:placeholder:mousedown');
            },
            mouseup: function (e) {
                scope.canvas.fire('object:placeholder:mouseup');
            },
            selected: function (e) {
                scope.canvas.fire('object:placeholder:maskSelected');
            },
            deselected: function (e) {
                scope.fire('deselected', e);
            },
            moving: function () {
                if (scope.saveVectorPosition || scope.limitMovement) {
                    const constrainedPoint = constrainPositionToBoundingBox(
                        scope,
                        scope._getControlRectangleEffectiveBox(),
                        this.left,
                        this.top
                    );
                    this.set({ left: constrainedPoint.x, top: constrainedPoint.y });
                    this.setCoords();
                }

                const distanceX = this.left - scope.left;
                const distanceY = this.top - scope.top;
                scope.setVectorOffset(distanceX, distanceY);
            },
            rotating: function () {
                // Rotate the element in steps
                this.angle = Math.ceil(this.angle / scope.rotationStep) * scope.rotationStep;

                if (scope.saveVectorPosition || scope.limitMovement) {
                    const lastAngle = this._lastValidAngle != null ? this._lastValidAngle : scope.angle;
                    const attemptedDelta = this.angle - lastAngle;
                    if (isRotationDeltaBlocked(scope, scope._getControlRectangleEffectiveBox(), attemptedDelta)) {
                        this.angle = lastAngle;
                        this.setCoords();
                        scope.setVectorAngle(this.angle - scope.angle);
                        return;
                    }
                }
                this._lastValidAngle = this.angle;

                scope.setVectorAngle(this.angle - scope.angle);
            },
            scaling: function () {
                if (scope.saveVectorPosition || scope.limitMovement) {
                    const proposedWidth = this.width * this.scaleX;
                    const proposedHeight = this.height * this.scaleY;
                    const scaleLimit = constrainDimensionToBoundingBox(scope, this, proposedWidth, proposedHeight);
                    // scaleLimit.scaleX/Y is relative to the already-scaled proposedWidth/proposedHeight
                    // (how much MORE it could grow), not an absolute scale - compound it with the
                    // current scale instead of overwriting, and only when it actually overflows (< 1).
                    if (scaleLimit.scaleX < 1 || scaleLimit.scaleY < 1) {
                        this.scaleX = this.scaleX * scaleLimit.scaleX;
                        this.scaleY = this.scaleY * scaleLimit.scaleY;
                    }
                }

                scope.setVectorScale(this.scaleX, this.scaleY);
            },
            scaled: function () {
                scope.saveInitValues();
            },
            moved: function () {
                scope.saveInitValues();
            },
            rotated: function () {
                scope.saveInitValues();
            }
        });

        this.on({
            added: function () {
                this.cornerSize = 0.025 * this.canvas.width;
            },
            removed: function () {
                this.canvas.remove(this._controlRectangle);
            },
            scaling: function (event) {
                // Only allow proportional scaling but allowing skew
                if (this.lockUniScalingWithSkew) {
                    let scale = 1;
                    if (event.transform.action === 'scaleX') {
                        scale = this.scaleX;
                    } else {
                        scale = this.scaleY;
                    }

                    this.scaleX = scale;
                    this.scaleY = scale;
                }
            },
            scaled: function () {
                this.setScale(this.width, this.height);
            },
            rotated: function () {
                this._syncOrResetControlRectangle();
            },
            'object:dblclick': function () {
                if (!this.preventEditMode) {
                    this._editMode = true;
                    this._enablingEditMode = true;

                    // Disable canvas selection
                    this._editModeDataBackup.originalCanvasSelection = this.canvas.selection;
                    this.canvas.selection = false;

                    // Make sure we can't use the controls when the mouse is over even when they are
                    //  not drawn
                    this._editModeDataBackup.controlsVisibilityBackup = JSON.parse(
                        JSON.stringify(this._getControlsVisibility())
                    );
                    this.setControlsVisibility({
                        bl: false,
                        br: false,
                        mb: false,
                        ml: false,
                        mr: false,
                        mt: false,
                        mtr: false,
                        tl: false,
                        tr: false
                    });

                    // Match selection settings
                    this._controlRectangle.set('cornerSize', this.cornerSize);
                    this._controlRectangle.set('transparentCorners', this.transparentCorners);
                    this._controlRectangle.set('centeredScaling', true);
                    this._controlRectangle.set('centeredRotation', true);
                    this._controlRectangle.set('skewX', this.skewX);
                    this._controlRectangle.set('skewY', this.skewY);

                    let scaleX = this.item(0).visible ? this.item(0).scaleX : this.item(1).scaleX;
                    let scaleY = this.item(0).visible ? this.item(0).scaleY : this.item(1).scaleY;
                    this._controlRectangle.set('scaleX', scaleX);
                    this._controlRectangle.set('scaleY', scaleY);

                    // Select the bounding rectangle object
                    this.canvas.add(this._controlRectangle);
                    this.canvas.setActiveObject(this._controlRectangle);

                    // Block object movement
                    this._editModeDataBackup.originalLockX = this.lockMovementX;
                    this._editModeDataBackup.originalLockY = this.lockMovementY;
                    this.lockMovementX = this.lockMovementY = true;

                    if (!this.silentEditMode) {
                        // Make the stroke red
                        this._editModeDataBackup.originalStroke = this.item(2).stroke;
                        this._editModeDataBackup.originalStrokeWidth = this.item(2).strokeWidth;

                        this.item(2).set('stroke', 'red');
                        this.item(2).set('strokeWidth', 5);
                    }

                    this._enablingEditMode = false;
                    this.canvas.requestRenderAll();
                }
            },
            deselected: function () {
                // When edit mode was forced (calling enableEditMode()) don't disable edit mode
                //  on deselection
                if (this._editMode && !this._forcedEditMode && !this._enablingEditMode) {
                    this._editMode = false;
                    this.canvas.selection = this._editModeDataBackup.originalCanvasSelection;
                    this.canvas.remove(this._controlRectangle);

                    // Restore controls
                    this.setControlsVisibility(this._editModeDataBackup.controlsVisibilityBackup);

                    // Restore movement lock
                    this.lockMovementX = this._editModeDataBackup.originalLockX;
                    this.lockMovementY = this._editModeDataBackup.originalLockY;

                    if (!this.silentEditMode) {
                        this.item(2).set('stroke', this._editModeDataBackup.originalStroke);
                        this.item(2).set('strokeWidth', this._editModeDataBackup.originalStrokeWidth);
                        this.set('dirty', true);
                    }

                    this.canvas.renderAll();
                }
                this.saveVectorPosition = false;
            }
        });

        this._adjustClippingPath();
    },

    _set: function (key, value) {
        if (key === 'id') {
            if (this._controlRectangle) {
                this._controlRectangle.id = value;
            }
            this.callSuper('_set', key, value);
        } else if (key === 'coverMaskArea') {
            this.item(0).set('coverMaskArea', value);
            this.item(1).set('coverMaskArea', value);
            this.callSuper('_set', key, value);
        } else if (key === 'angle') {
            this.callSuper('_set', key, value);
            this._syncOrResetControlRectangle();
        } else if (key === 'svgString') {
            // Callers like changeDynamicEPSPosition() re-set the same content every time the
            //  placeholder is (re)selected (e.g. to fix up clones with missing content), which
            //  would otherwise wipe out any manual double-click transform on every reselect.
            //  Only reset when the content is actually changing.
            const contentChanged = value !== this.svgString;
            this._contentLoadPromise = this.setSVGString(value);
            if (contentChanged) {
                this._resetControlRectangle();
            }
        } else if (key === 'svgImage') {
            // Same reasoning as the svgString branch above - avoid discarding a manual
            //  transform when the image content hasn't actually changed.
            const contentChanged = value !== this.item(0).svgImage;
            this._contentLoadPromise = this.setSvgImage(value);
            if (contentChanged) {
                this._resetControlRectangle();
            }
        } else if (key === 'pdf') {
            const pdfObj = this.item(1);
            const svgObj = this.item(0);

            pdfObj.set('visible', true);
            svgObj.set('visible', false);

            // Same reasoning as the svgString branch above - avoid discarding a manual
            //  transform when the pdf content hasn't actually changed.
            const contentChanged = value !== this.pdf;

            pdfObj.set('pdf', value);
            this.pdf = value;
            this.svgString = '';
            if (contentChanged) {
                this._resetControlRectangle();
            }
            this._contentLoadPromise = this._updateSelectionRectScale(pdfObj._renderPromise, pdfObj);
        } else if (key === 'width') {
            value = value || 0;

            // Set new dimensions
            this._objects.forEach((element) => {
                element.set('width', value);
                element.setCoords();
            });

            this.callSuper('_set', key, value);
            this._adjustClippingPath();
            // A frame resize must not wipe out an already-applied initial position: initWidth/
            //  initCenterX are ratios relative to this frame's own width/height (see
            //  saveInitValues), so they stay valid across a resize, but the content still needs
            //  to be re-scaled/re-offset for the new frame size - see _syncOrResetControlRectangle.
            this._syncOrResetControlRectangle();
        } else if (key === 'height') {
            value = value || 0;

            // Set new dimensions
            this._objects.forEach((element) => {
                element.set('height', value);
                element.setCoords();
            });

            this.callSuper('_set', key, value);
            this._adjustClippingPath();
            this._syncOrResetControlRectangle();
        } else if (key === 'svgColor') {
            this.item(0).set('svgColor', value);
            this.item(1).set('pdfColor', value);
            this.callSuper('_set', key, value);
        } else if (key === 'evented') {
            this._controlRectangle.set('evented', value);

            // If on edit mode, don't alter the evented attribute from the VectorPlaceholder, only from the
            //  selection rectangle
            if (!this._forcedEditMode && !this._editMode) {
                this.callSuper('_set', key, value);
            }
        } else if (key === 'stroke' || key === 'strokeWidth') {
            this.item(2).set(key, value);
            this.callSuper('_set', key, value);
        } else if (key === 'hasControls') {
            this._controlRectangle.set('hasControls', value);
            this.callSuper('_set', key, value);
        } else {
            this.callSuper('_set', key, value);
        }
    },

    /**
     * This enables edit mode to allow moving and zooming the vector within the element.
     * The only way to disable edit mode after using enableEditMode() is calling
     *  disableEditMode()
     */
    enableEditMode: function () {
        this._forcedEditMode = true;
        this.fire('object:dblclick');
    },

    disableEditMode: function () {
        this.fire('deselected');
    },

    /**
     * This method is intended to be called after the object was modified with a group
     *  selection. On fabric.js, elements events are not fired when inside a group, that's
     *  why this method must be called manually after exiting group selection
     */
    updateFromGroupScaling: function () {
        this.setScale(this.width, this.height);
    },

    setScale: function (width, height) {
        const w = width * this.scaleX,
            h = height * this.scaleY;

        // Set the new width and height
        this.scaleX = 1;
        this.scaleY = 1;
        this.set({ width: w, height: h });
    },

    setSvgImage: function (image) {
        this.item(1).set('visible', false);
        this.item(0).set('visible', true);

        this.item(0).set('svgImage', image);
        this.pdf = null;
        this.svgString = '';
        return this._updateSelectionRectScale(this.item(0)._renderPromise, this.item(0));
    },

    setSVGString: function (svg) {
        this.item(1).set('visible', false);
        this.item(0).set('visible', true);

        this.item(0).set('svgString', svg);
        this.pdf = null;
        this.svgString = svg;
        return this._updateSelectionRectScale(this.item(0)._renderPromise, this.item(0));
    },

    setVectorOffset(offsetX, offsetY) {
        const result = this._correctOffset(offsetX, offsetY);

        this.item(0).set('left', result.x);
        this.item(1).set('left', result.x);

        this.item(0).set('top', result.y);
        this.item(1).set('top', result.y);

        this._controlRectangle.set('left', this.left + offsetX);
        this._controlRectangle.set('top', this.top + offsetY);
        this._controlRectangle.setCoords();
    },

    setVectorAngle(angle) {
        this.item(0).set('angle', angle);
        this.item(1).set('angle', angle);

        this._controlRectangle.set('angle', angle + this.angle);
        this._controlRectangle.setCoords();
    },

    setVectorScale(scaleX, scaleY) {
        this.item(0).set('scaleX', scaleX);
        this.item(1).set('scaleX', scaleX);

        this.item(0).set('scaleY', scaleY);
        this.item(1).set('scaleY', scaleY);

        this._controlRectangle.set('scaleX', scaleX);
        this._controlRectangle.set('scaleY', scaleY);
        this._controlRectangle.setCoords();
    },

    // Programmatic (non-drag) counterparts of setVectorOffset/setVectorScale/setVectorAngle that
    //  also respect saveVectorPosition/limitMovement - for callers outside the moving/scaling/
    //  rotating handlers on _controlRectangle that would otherwise bypass the clamp entirely.
    //  Mirrors fabricImagePlaceholder.js's setEditModeImageOffset/setEditModeImageScale, and kept
    //  in parity with TurcosJSLib's copy of this class even though nothing in the admin app calls
    //  these yet - the storefront's engraver.js exposes button controls and a group-mirror sync
    //  that need them.
    setEditModeVectorOffset(offsetX, offsetY) {
        if (this.saveVectorPosition || this.limitMovement) {
            const proposedLeft = this.left + offsetX;
            const proposedTop = this.top + offsetY;
            const constrained = constrainPositionToBoundingBox(
                this,
                this._getControlRectangleEffectiveBox(),
                proposedLeft,
                proposedTop
            );
            offsetX = constrained.x - this.left;
            offsetY = constrained.y - this.top;
        }
        this.setVectorOffset(offsetX, offsetY);
    },

    setEditModeVectorScale(scaleX, scaleY) {
        if (this.saveVectorPosition || this.limitMovement) {
            const proposedWidth = this.width * scaleX;
            const proposedHeight = this.height * scaleY;
            const scaleLimit = constrainDimensionToBoundingBox(
                this,
                this._getControlRectangleEffectiveBox(),
                proposedWidth,
                proposedHeight
            );
            // scaleLimit.scaleX/Y is relative to the already-scaled proposedWidth/proposedHeight
            // (how much MORE it could grow), not an absolute scale - compound it with the
            // requested scale instead of overwriting, and only when it actually overflows (< 1).
            if (scaleLimit.scaleX < 1 || scaleLimit.scaleY < 1) {
                scaleX = scaleX * scaleLimit.scaleX;
                scaleY = scaleY * scaleLimit.scaleY;
            }
        }
        this.setVectorScale(scaleX, scaleY);
    },

    // Applies a rotation delta (in degrees) to the vector's content, clamped down to however much
    //  room is actually available, instead of letting it overshoot the placeholder's bounds. The
    //  programmatic counterpart to setEditModeVectorOffset/Scale above - callers just pass how
    //  much they want to rotate by, this derives and applies the resulting target angle itself
    //  instead of leaving that arithmetic to each caller (getEditModeParameters().angle is the
    //  content's own angle - constrainRotationToBoundingBox's deltas are relative to that same
    //  value, so no frame-angle adjustment is needed here).
    //  Returns the delta actually applied (0 if there was no room left in that direction).
    setEditModeVectorRotationDelta(delta) {
        if (this.saveVectorPosition || this.limitMovement) {
            delta = clampRotationDelta(this, this._getControlRectangleEffectiveBox(), delta);
        }
        if (delta) {
            const currentAngle = this.getEditModeParameters().angle;
            this.setVectorAngle(currentAngle + delta);
        }
        return delta;
    },

    getHeight() {
        return this.height * this.scaleY;
    },
    getWidth() {
        return this.width * this.scaleX;
    },

    getEditModeParameters() {
        return {
            offsetX: this._controlRectangle.left - this.left || 0,
            offsetY: this._controlRectangle.top - this.top || 0,
            scaleX: this._controlRectangle.scaleX || 1,
            scaleY: this._controlRectangle.scaleY || 1,
            angle: this._controlRectangle.angle || 0
        };
    },

    _updateSelectionRectScale: function (promise, item) {
        if (promise) {
            return promise.then(() => {
                this._controlRectangle.set('scaleX', item.scaleX);
                this._controlRectangle.set('scaleY', item.scaleY);
                this._controlRectangle.setCoords();

                if (this.hasInitValues()) {
                    this.setOffSetsBasedOnInitValues();
                }
            });
        }
    },

    /**
     * Compute the corrected X and Y displacement with the current object angle. Lots of math to
     *  end up with this equations, I can't add a picture of all the math I did but should be pretty
     *  straightforward to calculate them again if needed.
     *
     * @param {number} offsetX translation in the X axis
     * @param {number} offsetY translation in the Y axe
     */
    _correctOffset: function (offsetX, offsetY) {
        // Convert canvas coordinate to group relative coordinates
        const mGroup = this.calcTransformMatrix(true);
        const mCanvasObject = fabric.iMatrix;
        const mTotal = fabric.util.multiplyTransformMatrices(mGroup, mCanvasObject);
        const mInverse = fabric.util.invertTransform(mTotal);
        const result = fabric.util.transformPoint(new fabric.Point(this.left + offsetX, this.top + offsetY), mInverse);
        return result;
    },

    // Inverse of _correctOffset: converts a group-relative (local) point into an offset from
    //  this.left/this.top in absolute canvas coordinates.
    _inverseCorrectOffset: function (left, top) {
        const mGroup = this.calcTransformMatrix(true);
        const mCanvasObject = fabric.iMatrix;
        const mTotal = fabric.util.multiplyTransformMatrices(mGroup, mCanvasObject);
        const result = fabric.util.transformPoint(new fabric.Point(left, top), mTotal);
        return { x: result.x - this.left, y: result.y - this.top };
    },

    _resetControlRectangle: function () {
        const rect = this._controlRectangle;
        rect.set('left', this.left);
        rect.set('top', this.top);
        rect.set('angle', this.angle);
        rect.set('scaleX', 1);
        rect.set('scaleY', 1);
        rect.set('width', this.width);
        rect.set('height', this.height);

        this.setVectorOffset(0, 0);
        this.setVectorAngle(0);
        this.setVectorScale(1, 1);
    },

    // Keeps the control rectangle's frame (left/top/angle/width/height) tracking the placeholder's
    //  own frame without touching the content's current offset/scale/rotation. Used when the frame
    //  is resized after an initial position was already applied, so resizing doesn't discard it.
    _syncControlRectangleFrame: function () {
        const rect = this._controlRectangle;
        rect.set('left', this.left);
        rect.set('top', this.top);
        rect.set('angle', this.angle);
        rect.set('width', this.width);
        rect.set('height', this.height);
        rect.setCoords();
    },

    // Called whenever the placeholder's own frame changes (resize or rotation). If an initial
    //  position was already applied, keep the control rectangle's frame tracking in sync AND
    //  re-derive the content's offset/scale/rotation for the new frame geometry (initWidth/
    //  initCenterX/etc. are ratios relative to the frame - see saveInitValues - so they stay
    //  numerically valid across a frame change, but the actual pixel offset/scale they resolve to
    //  does change and must be recomputed, or the content silently drifts out of its saved
    //  position). Otherwise there's nothing to preserve, so just reset to identity.
    _syncOrResetControlRectangle: function () {
        if (this.hasInitValues()) {
            this._syncControlRectangleFrame();
            this.setOffSetsBasedOnInitValues();
        } else {
            this._resetControlRectangle();
        }
    },

    // The control rectangle keeps its zoom as scaleX/scaleY instead of baking it into
    //  width/height, so geometryUtils (which reads width/height directly) needs a plain
    //  object with the effective (scaled) size.
    _getControlRectangleEffectiveBox: function () {
        const rect = this._controlRectangle;
        return {
            left: rect.left,
            top: rect.top,
            width: rect.width * rect.scaleX,
            height: rect.height * rect.scaleY,
            angle: rect.angle
        };
    },

    hasInitValues: function () {
        return (
            this.initWidth != null &&
            this.initHeight != null &&
            this.initCenterX != null &&
            this.initCenterY != null &&
            this.initRotation != null
        );
    },

    resetVectorPositionAndSize: function () {
        this._resetControlRectangle();
    },

    resetInitPosition: function () {
        this.initWidth = null;
        this.initHeight = null;
        this.initCenterX = null;
        this.initCenterY = null;
        this.initRotation = null;

        this.resetVectorPositionAndSize();
        this.setEditedProp?.('initVectorPosition');
    },

    // Captures the control rectangle's current position/size/rotation as the placeholder's
    //  initial position. Only runs while in "Set Initial Size & Position" authoring mode.
    //  Stored as ratios relative to this frame's own current width/height/rotation instead of
    //  pixel values, so they reproduce correctly regardless of the absolute pixel size of the
    //  frame this placeholder is rendered on (admin canvas vs. step2 preview vs. production
    //  file vs. storefront all render the very same physical placeholder at different pixel
    //  sizes) - item(0)/item(1).width is always kept equal to this.width by the width/height
    //  _set branches above, so activeItem.scaleX/Y is already such a ratio (the content's zoom
    //  relative to the frame); left/top are group-local (relative to the frame's own
    //  untransformed center) but still in absolute pixels, so those are additionally divided by
    //  the frame's own width/height to make them resolution-independent too.
    saveInitValues: function () {
        if (!this.saveVectorPosition) {
            return;
        }

        const activeItem = this.item(0).visible ? this.item(0) : this.item(1);
        this.initWidth = activeItem.scaleX;
        this.initHeight = activeItem.scaleY;
        this.initCenterX = this.width ? activeItem.left / this.width : 0;
        this.initCenterY = this.height ? activeItem.top / this.height : 0;
        this.initRotation = activeItem.angle;

        this.setEditedProp?.('initVectorPosition');
    },

    // Snaps the currently loaded vector content to the previously saved initial position.
    //  Called whenever new SVG/PDF content finishes loading into the placeholder.
    setOffSetsBasedOnInitValues: function () {
        if (!this.hasInitValues()) {
            return;
        }

        // initCenterX/initCenterY are frame-local coordinates expressed as a ratio of this
        //  frame's own current width/height (see saveInitValues) - scale back up to this
        //  frame's current pixel size, then convert to an offset from the frame's own left/top
        //  via _inverseCorrectOffset (the inverse of _correctOffset, used the same way the
        //  'moving' handler above derives offsets live) for setVectorOffset().
        const offset = this._inverseCorrectOffset(this.initCenterX * this.width, this.initCenterY * this.height);

        this.setVectorScale(this.initWidth, this.initHeight);
        this.setVectorOffset(offset.x, offset.y);
        this.setVectorAngle(this.initRotation);
    },

    _adjustClippingPath: function () {
        if (!this.clipPath) {
            this.set(
                'clipPath',
                new fabric.Rect({
                    originX: 'center',
                    originY: 'center'
                })
            );
        }

        this.clipPath.set('width', this.width);
        this.clipPath.set('height', this.height);
    },

    _getPdfRenderPromise: function () {
        var pdfObj = this.item(1);
        return pdfObj._renderPromise;
    },

    _getSvgRenderPromise: function () {
        var svgObj = this.item(0);
        return svgObj._renderPromise;
    },

    toObject: function (propertiesToInclude) {
        const additionalProperties = [
            'id',
            'uuid',
            'strokeWidth',
            'strokeDashArray',
            'centeredRotation',
            'selectedPosition',
            'selectedColorPosition',
            'vectors',
            'colors',
            'vectorLibraryId',
            'colorLibraryId',
            'skewX',
            'skewY',
            'lockUniScalingWithSkew',
            'evented',
            'selectable',
            'visible',
            'locked',
            'name',
            'coverMaskArea',
            'rotationStep',
            'cornerSize',
            'transparentCorners',
            'zIndex',
            'allowUserUpload',
            'applyColoring',
            'svgString',
            'svgColor',
            'groupId',
            'pageEpsId',
            'starMapConfig',
            'qrConfig',
            'type',
            'allowEdit',
            'isSolidBackground',
            'allowSwapLibraryPosition',
            'initWidth',
            'initHeight',
            'initCenterX',
            'initCenterY',
            'initRotation',
            'limitMovement'
        ].concat(propertiesToInclude);

        return this.callSuper('toObject', additionalProperties);
    }
});

fabric.VectorPlaceholder.fromObject = function (object, callback) {
    object.clipPath = undefined;
    const newObject = new fabric.VectorPlaceholder(object);
    callback(newObject);
};
